import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/**
 * Convert PDF page to base64 image
 */
async function pdfPageToBase64(pdfPage, scale = 2.0) {
  const viewport = pdfPage.getViewport({ scale });
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  canvas.height = viewport.height;
  canvas.width = viewport.width;

  const renderContext = {
    canvasContext: context,
    viewport: viewport
  };

  await pdfPage.render(renderContext).promise;

  // Convert canvas to base64
  return canvas.toDataURL('image/png').split(',')[1];
}

/**
 * Extract planning categories using OpenAI Vision API
 * @param {File} pdfFile - The PDF file to analyze
 * @param {Array} employees - List of active employees with their names
 * @returns {Promise<Object>} - Structured categories data
 */
export async function extractPlanningWithOpenAI(pdfFile, employees) {
  console.log('🤖 Starting OpenAI Vision analysis...');

  const apiKey = process.env.REACT_APP_OPENAI_API_KEY;

  if (!apiKey) {
    throw new Error('REACT_APP_OPENAI_API_KEY is not configured in environment variables');
  }

  try {
    // 1. Load PDF
    const arrayBuffer = await pdfFile.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    console.log(`PDF loaded: ${pdf.numPages} pages`);

    // 2. Get the last page (where the planning table usually is)
    // IMPORTANT: The table is always on the LAST page, not the first
    const pageNum = pdf.numPages;
    const page = await pdf.getPage(pageNum);

    // 3. Convert page to base64 image
    console.log('Converting PDF page to image...');
    const base64Image = await pdfPageToBase64(page, 3.0);

    // 4. Create employee names list with aliases for the prompt
    const employeeNames = employees.map(emp => {
      if (emp.aliases && emp.aliases.length > 0) {
        return `${emp.name} (alias: ${emp.aliases.join(', ')})`;
      }
      return emp.name;
    }).join('\n');

    // 5. Call OpenAI Vision API
    console.log('Calling OpenAI Vision API...');
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages: [
          {
            role: 'user',
            content: [
              {
                type: 'text',
                text: `Analyse ce planning PDF et crée des listes par statut selon ces règles.

📋 LISTE DES EMPLOYÉS ACTIFS (avec leurs alias possibles) :
${employeeNames}

🎯 MÉTHODE EN 2 ÉTAPES :

## ÉTAPE 1 - EXTRAIRE LE TABLEAU (en bas du document) :
Identifier les personnes dans chaque colonne du tableau :

- **ASTREINTE** : Colonne "Astreinte - Aides départ et retour des bateaux"
- **BASE** : Colonne "BASE" (peut contenir "AM:", "PM:" → extrais juste le nom)
- **SEABOB** : Colonne "SEABOB"
- **JETSKI** : Colonne "JETSKI"
- **BUREAU** : Colonne "BUREAU" (peut contenir des horaires comme "7H GLADYS" → extrais "GLADYS")
- **ST_KITTS** : Colonne "ST KITTS"
- **OFF** : Colonne "OFF"

## ÉTAPE 2 - IDENTIFIER AT_SEA (corps du planning) :

**AT_SEA = TOUTES les personnes mentionnées dans le document qui NE sont PAS dans le tableau.**

Chercher les noms dans :
- Les équipages des sorties bateaux (ex: "ANTHO - PIERRE JEAN - MOWGLI")
- Les transferts (ex: "VAL - ENZO - MATHEO")
- Les missions JETSKI du corps du planning (ex: "DEYDEY/LUDO")
- Toute mention de nom avec un bateau, horaire ou client

⚠️ RÈGLE IMPORTANTE :
**Si un nom apparaît dans le tableau ET dans les sorties → le mettre dans les DEUX catégories**
Exemple : ENZO peut être BASE (tableau) ET AT_SEA (transfer)

🎯 EXEMPLE CONCRET :

**DOCUMENT :**

CORPS DU PLANNING (sorties) :
09:00 - SUNSHINE - ANTHO - PIERRE JEAN - MOWGLI
14:00 - TRANSFER SBH-SXM - VAL - ENZO
JETSKI: DEYDEY/LUDO

TABLEAU (en bas) :
ASTREINTE | BASE      | SEABOB | JETSKI | BUREAU    | ST KITTS  | OFF
JAROD     | AM: TANIA | TOM    | LUDO   | 7H GLADYS | ANTOINE B | FLO
LEO       | PM: ENZO  |        |        | 8H PIERRE | COLIN     | LAURIE

**RÉSULTAT JSON :**

{
  "categories": {
    "AT_SEA": ["ANTHO", "PIERRE JEAN", "MOWGLI", "VAL", "ENZO", "DEYDEY", "LUDO"],
    "ASTREINTE": ["JAROD", "LEO"],
    "BASE": ["TANIA", "ENZO"],
    "SEABOB": ["TOM"],
    "JETSKI": ["LUDO"],
    "BUREAU": ["GLADYS", "PIERRE"],
    "ST_KITTS": ["ANTOINE B", "COLIN"],
    "OFF": ["FLO", "LAURIE"]
  }
}

Note : ENZO apparaît 2 fois (BASE + AT_SEA), LUDO apparaît 2 fois (JETSKI + AT_SEA), PIERRE apparaît 2 fois (BUREAU + AT_SEA)

📤 FORMAT DE RÉPONSE (JSON STRICT, aucun texte avant/après) :
{
  "categories": {
    "AT_SEA": ["nom1", "nom2"],
    "ASTREINTE": ["nom3"],
    "BASE": ["nom4"],
    "SEABOB": ["nom5"],
    "JETSKI": ["nom6"],
    "BUREAU": ["nom7"],
    "ST_KITTS": ["nom8"],
    "OFF": ["nom9"]
  },
  "date": "YYYY-MM-DD"
}

✅ CHECKLIST FINALE :
1. ✅ Scanne le CORPS du planning → extrais TOUS les noms associés aux sorties/transfers/missions → AT_SEA
2. ✅ Scanne le TABLEAU en bas → extrais les noms de chaque colonne
3. ✅ Une personne peut être dans PLUSIEURS catégories (tableau + AT_SEA)
4. ✅ Utilise les noms complets de la liste (pas les alias)
5. ✅ Nettoie les préfixes (AM:, PM:, 7H, etc.)
6. ✅ Extrais la date du planning (format YYYY-MM-DD)`
              },
              {
                type: 'image_url',
                image_url: {
                  url: `data:image/png;base64,${base64Image}`,
                  detail: 'high'
                }
              }
            ]
          }
        ],
        max_tokens: 2000,
        temperature: 0.1
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('OpenAI API error:', errorData);
      throw new Error(`OpenAI API error: ${errorData.error?.message || response.statusText}`);
    }

    const data = await response.json();
    console.log('OpenAI response:', data);

    // 6. Parse the response
    const content = data.choices[0].message.content.trim();

    // Remove markdown code blocks if present
    let jsonContent = content;
    if (content.startsWith('```json')) {
      jsonContent = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    } else if (content.startsWith('```')) {
      jsonContent = content.replace(/```\n?/g, '').trim();
    }

    const result = JSON.parse(jsonContent);

    console.log('✅ OpenAI analysis complete:', result);

    // 7. Validate and normalize the result
    const normalizedResult = normalizeCategories(result, employees);

    return normalizedResult;

  } catch (error) {
    console.error('❌ OpenAI PDF analysis error:', error);
    throw error;
  }
}

/**
 * Normalize and validate the categories returned by OpenAI
 */
function normalizeCategories(result, employees) {
  const validCategories = ['AT_SEA', 'ASTREINTE', 'BASE', 'SEABOB', 'JETSKI', 'BUREAU', 'ST_KITTS', 'OFF'];

  // Initialize all categories
  const normalized = {
    categories: {
      AT_SEA: [],
      ASTREINTE: [],
      BASE: [],
      SEABOB: [],
      JETSKI: [],
      BUREAU: [],
      ST_KITTS: [],
      OFF: []
    },
    date: result.date || null
  };

  // Validate categories exist
  if (!result.categories) {
    console.error('No categories found in OpenAI response');
    return normalized;
  }

  // Add employees to their categories
  for (const category of validCategories) {
    if (result.categories[category] && Array.isArray(result.categories[category])) {
      normalized.categories[category] = result.categories[category];
    }
  }

  // Note: We don't auto-assign missing employees anymore
  // They will be handled in the UI verification step

  return normalized;
}

/**
 * Extract planning date from PDF text (fallback)
 */
export async function extractPlanningDate(pdfFile) {
  const arrayBuffer = await pdfFile.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  const page = await pdf.getPage(1);
  const textContent = await page.getTextContent();
  const text = textContent.items.map(item => item.str).join(' ');

  // Try to extract date from text
  const datePattern = /(\d{1,2})\s+(JANVIER|FEVRIER|FÉVRIER|MARS|AVRIL|MAI|JUIN|JUILLET|AOUT|AOÛT|SEPTEMBRE|OCTOBRE|NOVEMBRE|DECEMBRE|DÉCEMBRE)/i;
  const match = text.match(datePattern);

  if (match) {
    const day = match[1];
    const monthText = match[2];

    const monthMap = {
      'JANVIER': 0, 'FEVRIER': 1, 'FÉVRIER': 1, 'MARS': 2, 'AVRIL': 3,
      'MAI': 4, 'JUIN': 5, 'JUILLET': 6, 'AOUT': 7, 'AOÛT': 7,
      'SEPTEMBRE': 8, 'OCTOBRE': 9, 'NOVEMBRE': 10, 'DECEMBRE': 11, 'DÉCEMBRE': 11
    };

    const month = monthMap[monthText.toUpperCase()];
    const currentYear = new Date().getFullYear();

    const date = new Date(currentYear, month, parseInt(day));
    return date.toISOString().split('T')[0]; // YYYY-MM-DD
  }

  return null;
}

export default {
  extractPlanningWithOpenAI,
  extractPlanningDate
};
